import React, { useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useForm } from "react-hook-form";
import { errorMsg, success } from "../../../utils/notifications";
import {
  createDefaultPricing,
  updateDefaultPricing,
} from "../../../api/settings.api";
import { FiX } from "react-icons/fi";

const AddDefaultPricing = ({
  open,
  setOpen,
  fetchDefaultPricings,
  editData,
  setEditData,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  useEffect(() => {
    if (open) {
      if (editData) {
        reset({
          pricePerWeek: editData.pricePerWeek,
          isActive: editData.isActive,
        });
      } else {
        reset({
          pricePerWeek: "",
          isActive: true,
        });
      }
    }
  }, [open, editData, reset]);

  const closeModal = () => {
    setOpen(false);
    setEditData(null);
    // Reset form with default values when closing
    reset({
      pricePerWeek: "",
      isActive: true,
    });
  };

  const onSubmit = async (data) => {
    try {
      // Convert pricePerWeek to number
      const formData = {
        ...data,
        pricePerWeek: parseFloat(data.pricePerWeek),
      };

      if (editData) {
        await updateDefaultPricing(editData._id, formData);
        success("Success!", "Default pricing updated successfully!");
      } else {
        await createDefaultPricing(formData);
        success("Success!", "Default pricing created successfully!");
      }
      fetchDefaultPricings();
      closeModal();
    } catch (error) {
      errorMsg(
        "Error",
        error.response?.data?.message || "Something went wrong"
      );
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all w-full max-w-md sm:my-8 sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                    onClick={closeModal}
                  >
                    <span className="sr-only">Close</span>
                    <FiX className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 w-full text-center sm:mt-0 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold leading-6 text-gray-900"
                    >
                      {editData
                        ? "Edit Default Pricing"
                        : "Add New Default Pricing"}
                    </Dialog.Title>
                    <div className="mt-4">
                      <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="space-y-4">
                          <div>
                            <label
                              htmlFor="pricePerWeek"
                              className="block text-sm font-medium text-gray-700"
                            >
                              Price Per Week (€){" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              id="pricePerWeek"
                              step="0.01"
                              min="0"
                              {...register("pricePerWeek", {
                                required: true,
                                min: 0,
                              })}
                              className={`block w-full rounded-md border shadow-sm focus:border-yellow-500 focus:ring-yellow-500 text-base px-4 py-3 sm:text-sm ${
                                errors.pricePerWeek
                                  ? "border-red-500"
                                  : "border-gray-300"
                              }`}
                              placeholder="Enter price per week"
                            />
                            {errors.pricePerWeek && (
                              <p className="mt-1 text-sm text-red-600">
                                {errors.pricePerWeek.type === "required"
                                  ? "Price is required"
                                  : "Price must be a positive number"}
                              </p>
                            )}
                          </div>

                          {editData && (
                            <div>
                              <label
                                htmlFor="isActive"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Status
                              </label>
                              <select
                                id="isActive"
                                {...register("isActive")}
                                className="block w-full rounded-md px-4 py-3 border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                              >
                                <option value={true}>Active</option>
                                <option value={false}>Inactive</option>
                              </select>
                            </div>
                          )}
                        </div>

                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-3">
                          <button
                            type="button"
                            className="inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto"
                            onClick={closeModal}
                            disabled={isSubmitting}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="inline-flex w-full justify-center rounded-md bg-yellow-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 sm:w-auto"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <span className="flex items-center">
                                <svg
                                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                {editData ? "Updating..." : "Saving..."}
                              </span>
                            ) : editData ? (
                              "Update"
                            ) : (
                              "Save"
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddDefaultPricing;
